import React, { useState } from 'react';
import { CheckPicker, Checkbox, Message, Placeholder } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { usePublishSurvey, useRemindSurveyRecipient, useSurveyPeople, useSurveyRecipients } from '../../hooks/useSurveys';
import { showToast } from '../../utils/toast';
import { copySurveyLink } from './SurveyLinkButton';

export default function SendSurveyModal({ survey, onClose }) {
  const peopleQuery = useSurveyPeople();
  const recipientsQuery = useSurveyRecipients(survey.id);
  const publish = usePublishSurvey();
  const remind = useRemindSurveyRecipient();
  const savedLinkAccess = survey.state === 'draft' || (survey.allowLinkResponses ?? survey.audience === 'link');
  const [audience, setAudience] = useState('selected');
  const [allowLinkResponses, setAllowLinkResponses] = useState(savedLinkAccess);
  const [recipientIds, setRecipientIds] = useState([]);
  const [error, setError] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const busy = publish.isPending || isWorking;
  const people = peopleQuery.data || [];
  const history = recipientsQuery.data || [];
  const alreadyIncluded = new Set(history.filter((person) => person.sentAt || person.responded).map((person) => person.employeeId));
  const pendingCount = audience === 'all'
    ? people.filter((person) => !alreadyIncluded.has(person.id)).length
    : recipientIds.filter((id) => !alreadyIncluded.has(id)).length;
  const loading = peopleQuery.isLoading || recipientsQuery.isLoading;
  const loadError = peopleQuery.error || recipientsQuery.error;
  const accessOnly = survey.state !== 'draft' && pendingCount === 0 && allowLinkResponses !== savedLinkAccess;
  const copyOnly = allowLinkResponses && pendingCount === 0;

  const send = async () => {
    setError('');
    setIsWorking(true);
    try {
      if (!copyOnly || survey.state === 'draft' || !savedLinkAccess) {
        await publish.mutateAsync({
          id: survey.id,
          audience: copyOnly ? 'selected' : audience,
          allowLinkResponses,
          recipientIds: !copyOnly && audience === 'selected' ? recipientIds : [],
          updatedAt: survey.updatedAt,
        });
      }
      if (copyOnly) await copySurveyLink(survey.id);
      showToast('success', copyOnly ? 'Survey link copied.' : accessOnly ? 'Survey link access updated.' : 'Survey invitations queued.');
      onClose();
    } catch (failure) {
      setError(String(failure?.message || failure || (copyOnly ? 'Unable to copy the survey link.' : 'Unable to send the survey.')));
    } finally {
      setIsWorking(false);
    }
  };

  // A nudge: goes straight away, as often as it's asked for.
  const sendReminder = async (person) => {
    setError('');
    try {
      await remind.mutateAsync({ id: survey.id, employeeId: person.employeeId });
      showToast('success', `Reminder sent to ${person.name}.`);
    } catch (failure) {
      setError(String(failure?.message || failure || 'Unable to send the reminder.'));
    }
  };

  return (
    <Modal open onClose={busy ? undefined : onClose} size="md" overflow backdrop={busy ? 'static' : true} keyboard={!busy}>
      <Modal.Header closeDisabled={busy}>
        <Modal.Title>Send out survey</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h3 className="svb-send__title">{survey.title}</h3>
        {error || loadError ? <Message type="error" showIcon>{error || String(loadError?.message || loadError)}</Message> : null}
        <div className="svb-field">
          <span className="bdc-modal__label">Who it goes to</span>
          <div className="svb-send__audience">
            <div className="svb-segment" role="radiogroup" aria-label="Who it goes to">
              {[
                { value: 'all', label: 'All Employees' },
                { value: 'selected', label: 'Selected people' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={audience === option.value}
                  className={`svb-segment__option${audience === option.value ? ' is-active' : ''}`}
                  onClick={() => setAudience(option.value)}
                  disabled={busy}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Checkbox
              checked={allowLinkResponses}
              onChange={(_value, checked) => setAllowLinkResponses(checked)}
              disabled={busy}
            >
              Anyone with the link
            </Checkbox>
          </div>
          {audience === 'selected' ? (
            <CheckPicker
              block
              data={people.map((person) => ({ value: person.id, label: person.name, email: person.email }))}
              value={recipientIds}
              onChange={(ids) => setRecipientIds(ids || [])}
              disabledItemValues={[...alreadyIncluded]}
              disabled={loading || busy}
              placeholder="Pick people"
              aria-label="Recipients"
              searchBy={(keyword, label, person) => `${label} ${person.email || ''}`.toLowerCase().includes(keyword.toLowerCase())}
              renderMenuItem={(label, person) => (
                <span className="svb-person">
                  <span>{label}{alreadyIncluded.has(person.value) ? ' (already included)' : ''}</span>
                  {person.email ? <span className="svb-person__email">{person.email}</span> : null}
                </span>
              )}
              countable
            />
          ) : null}
          <p className="svb-hint">
            {`${pendingCount} ${pendingCount === 1 ? 'person' : 'people'} to notify. Previous recipients keep access and are only emailed again if you remind them.`}
            {allowLinkResponses ? ' Anyone with the link must sign in.' : ''}
          </p>
        </div>

        <h3 className="svb-send__heading">Previous recipients ({history.length})</h3>
        {loading ? <Placeholder.Paragraph rows={3} active /> : history.length ? (
          <div className="nsi-table-wrap svb-send__history">
            <table className="nsi-table bdc-table--narrow">
              <thead><tr><th>Person</th><th>Email delivery</th><th>Response</th><th aria-label="Remind" /></tr></thead>
              <tbody>
                {history.map((person) => (
                  <tr key={person.employeeId}>
                    <td><strong>{person.name}</strong><div className="svb-person__email">{person.email}</div></td>
                    <td>
                      {person.sentAt ? `Sent ${new Date(person.sentAt).toLocaleString()}` : 'Not emailed yet'}
                      {person.remindedAt ? <div className="svb-person__email">Reminded {new Date(person.remindedAt).toLocaleString()}</div> : null}
                    </td>
                    <td>{person.responded ? 'Responded' : 'Not submitted'}</td>
                    <td className="nsi-table__right">
                      {person.canRemind ? (
                        <Button
                          size="sm"
                          icon="bell"
                          onClick={() => sendReminder(person)}
                          loading={remind.isPending && remind.variables?.employeeId === person.employeeId}
                          disabled={busy || remind.isPending}
                          aria-label={`Remind ${person.name}`}
                        >
                          Remind
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="svb-hint">No invitations sent yet.</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" icon={copyOnly ? 'link-45deg' : undefined} onClick={send} loading={busy} disabled={loading || Boolean(loadError) || (!allowLinkResponses && pendingCount === 0 && !accessOnly)}>
          {copyOnly ? 'Copy link' : accessOnly ? 'Save access' : 'Send out'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}